import { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import { Tooltip } from "react-tooltip";
import { useDispatch } from "react-redux";
import {
  holdPatientMedicineAction,
  getPatientPrescriptionsAction,
} from "../../../../../store/actions/PrescriptionAction";
import Loader from "../../../Common/Loader";
import icon2 from "../../../../../../src/assets/images/icons8-info-80.png";
import {
  handleSuccess,
  showErrorAlert,
} from "../../../../../services/ErrorHandlingService";

const PrescriptionDetailModal = ({
  show,
  onHide,
  selectedPrescription,
  patientDetails,
  // onEditPrescription,
}) => {
  const dispatch = useDispatch();
  const [selectedMedicines, setSelectedMedicines] = useState(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Debug: Log the prescription data when modal opens
  // ✅ Hook 1
  useEffect(() => {
    if (show && selectedPrescription?.medicines) {
      selectedPrescription.medicines.forEach((medicine, index) => {
        console.log(`Medicine ${index}:`, medicine);
      });
    }
  }, [show, selectedPrescription]);

  // ✅ Hook 2
  useEffect(() => {
    const interval = setInterval(() => {
      const backdrop = document.querySelector(".second-backdrop");
      if (backdrop) {
        backdrop.style.zIndex = 1990;
        backdrop.style.backgroundColor = "rgba(0,0,0,0.6)";
        clearInterval(interval);
      }
    }, 50);

    return () => clearInterval(interval);
  }, []);

  // ❌ NEVER RETURN BEFORE HOOKS
  // if (!selectedPrescription) return null;

  // ✅ SAFE CONDITIONAL UI
  if (!selectedPrescription) {
    return (
      <Modal show={show} onHide={onHide} size="xl" centered>
        <div className="p-4 text-center">Loading Prescription...</div>
      </Modal>
    );
  }


  // Separate medicines into custom and non-custom
  const regularMedicines =
    selectedPrescription?.medicines?.filter(
      (medicine) => medicine.is_custom === "No"
    ) || [];
  const customMedicines =
    selectedPrescription?.medicines?.filter(
      (medicine) => medicine.is_custom === "Yes"
    ) || [];

  // Fixed: Use medicine_id consistently for checkbox handling
  const handleMedicineCheckboxChange = (medicine, isChecked) => {
    // Check if medicine_id exists
    if (!medicine.medicine_id) {
      console.error("ERROR: medicine_id is undefined or null!");
      showErrorAlert("Medicine ID is missing - cannot select for hold");
      return;
    }

    // Use medicine_id from the medicine object
    const medicineId = String(medicine.medicine_id);

    const newSelectedMedicines = new Set(selectedMedicines);

    if (isChecked) {
      newSelectedMedicines.add(medicineId);
    } else {
      newSelectedMedicines.delete(medicineId);
    }

    setSelectedMedicines(newSelectedMedicines);
  };


  const canEditPrescription = () => {
    if (!selectedPrescription?.date) return false;

    // Convert "2025-11-20 00:00:00" → "2025-11-20T00:00:00"
    const formattedDate = selectedPrescription.date.replace(" ", "T");

    const createdDate = new Date(formattedDate);
    const today = new Date();

    const isSameDay =
      createdDate.getFullYear() === today.getFullYear() &&
      createdDate.getMonth() === today.getMonth() &&
      createdDate.getDate() === today.getDate();

    // delivered check
    const anyMedicineDelivered = selectedPrescription?.medicines?.some(
      (m) => Number(m.delivered_qty) > 0
    );

    return isSameDay && !anyMedicineDelivered;
  };


  const handleMarkAsHold = async () => {
    if (selectedMedicines.size === 0) {
      showErrorAlert("Please select at least one medicine to hold");
      return;
    }

    if (isSubmitting) {
      console.log("Already submitting, returning...");
      return;
    }

    setIsSubmitting(true);
    try {
      const prescription_id = selectedPrescription.prescriptionId;
      const selectedMedicineIds = Array.from(selectedMedicines);
      const hold_medicines = selectedMedicineIds.join("|");

      // Additional validation
      if (!prescription_id) {
        console.error("ERROR: Prescription ID is missing!");
        showErrorAlert("Prescription ID is missing");
        return;
      }

      if (!hold_medicines || hold_medicines.trim() === "") {
        console.error("ERROR: Hold medicines string is empty!");
        showErrorAlert("No medicines selected for hold");
        return;
      }

      const result = await dispatch(
        holdPatientMedicineAction(prescription_id, hold_medicines)
      );

      if (result && result.success) {
        handleSuccess("Medicines marked as hold successfully");
        setSelectedMedicines(new Set()); // Clear selections

        // Show loader immediately
        setShowLoader(true);

        // Refresh prescription data
        const patientId = patientDetails?.patient_id;
        if (patientId) {
          await dispatch(
            getPatientPrescriptionsAction({ patient_id: patientId })
          );
        }

        // Hide loader and close modal after a delay
        setTimeout(() => {
          setShowLoader(false);
          onHide();
        }, 1500);
      } else {
        console.error("Action failed:", result);
        showErrorAlert(result?.error || "Failed to hold medicines");
      }
    } catch (error) {
      console.error("Error holding medicines:", error);
      showErrorAlert("Failed to hold medicines");
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleEdit = () => {
    setIsEditMode(true);
  };
  const onFieldChange = (medicine, field, value) => {
    const updatedMedicines = selectedPrescription.medicines.map((m) =>
      m.medicine_id === medicine.medicine_id
        ? { ...m, [field]: value }
        : m
    );
    selectedPrescription.medicines = updatedMedicines;

    setSelectedMedicines(new Set([...selectedMedicines]));
  };
  const handleSaveEdit = async () => {
    try {
      console.log("Final Updated Prescription:", selectedPrescription);

      // TODO: call update API here
      // await dispatch(updatePrescriptionAction(selectedPrescription));

      handleSuccess("Prescription Updated Successfully!");
      setIsEditMode(false);
    } catch (err) {
      console.error("Update failed:", err);
      showErrorAlert("Failed to update prescription");
    }
  };


  const handleModalClose = () => {
    setSelectedMedicines(new Set()); // Clear selections when modal closes
    setShowLoader(false); // Reset loader state
    onHide();
  };

  // Check if medicine is already on hold
  const isMedicineOnHold = (medicine) => {
    return medicine.is_hold === "Yes";
  };

  // Fixed: Check if checkbox should be checked using medicine_id
  const isCheckboxChecked = (medicine) => {
    if (!medicine.medicine_id) return false;
    const medicineId = String(medicine.medicine_id);
    return isMedicineOnHold(medicine) || selectedMedicines.has(medicineId);
  };

  // Check if checkbox should be disabled (if medicine is on hold)
  const isCheckboxDisabled = (medicine) => {
    return isMedicineOnHold(medicine);
  };

  // Common table header component
  const TableHeader = ({ isCustom = false }) => (
    <thead
      style={{
        position: "sticky",
        top: 0,
        backgroundColor: "#f8f9fa",
        zIndex: 10,
        borderBottom: "2px solid #ddd",
      }}
    >
      <tr className="table-info">
        <th
          style={{
            padding: "10px",
            color: "black",
            fontWeight: "bold",
            whiteSpace: "nowrap",
            textAlign: "center",
            verticalAlign: "middle",
          }}
        >
          Medicine Name
        </th>
        <th
          style={{
            padding: "10px",
            color: "black",
            fontWeight: "bold",
            whiteSpace: "nowrap",
            textAlign: "center",
            verticalAlign: "middle",
          }}
        >
          Dose
        </th>
        <th
          style={{
            padding: "10px",
            color: "black",
            fontWeight: "bold",
            textAlign: "center",
            verticalAlign: "middle",
            whiteSpace: "nowrap",
          }}
        >
          Frequency
        </th>
        <th
          style={{
            padding: "10px",
            color: "black",
            fontWeight: "bold",
            whiteSpace: "nowrap",
            textAlign: "center",
            verticalAlign: "middle",
          }}
        >
          Quantity
        </th>
        <th
          style={{
            padding: "10px",
            color: "black",
            fontWeight: "bold",
            whiteSpace: "nowrap",
            textAlign: "center",
            verticalAlign: "middle",
          }}
        >
          Delivered Qty
        </th>
        {isCustom && (
          <th
            style={{
              padding: "10px",
              color: "black",
              fontWeight: "bold",
              whiteSpace: "nowrap",
              textAlign: "center",
              verticalAlign: "middle",
            }}
          >
            Duration
          </th>
        )}
        <th
          style={{
            padding: "10px",
            color: "black",
            fontWeight: "bold",
            whiteSpace: "nowrap",
            textAlign: "center",
            verticalAlign: "middle",
          }}
        >
          How to Take?
        </th>
        <th
          style={{
            padding: "10px",
            color: "black",
            fontWeight: "bold",
            whiteSpace: "nowrap",
            textAlign: "center",
            verticalAlign: "middle",
          }}
        >
          Anupan
        </th>
        <th
          style={{
            padding: "10px",
            color: "black",
            fontWeight: "bold",
            whiteSpace: "nowrap",
            textAlign: "center",
            verticalAlign: "middle",
          }}
        >
          Hold
        </th>

      </tr>
    </thead>
  );

  // Common table row component
  const MedicineRow = ({ medicine, index, isCustom = false }) => (
    <tr
      key={`${isCustom ? "custom" : "regular"}-${medicine.medicine_id || medicine.prescription_medicine_id || index
        }-${index}`}
    >
      <td
        style={{
          padding: "10px",
          textAlign: "center",
          verticalAlign: "middle",
        }}
      >
        {medicine.medicine_name || medicine.medicineName}
      </td>
      <td
        style={{
          padding: "10px",
          textAlign: "center",
          verticalAlign: "middle",
        }}
      >
        {medicine.dose}
      </td>
      <td
        style={{
          padding: "10px",
          textAlign: "center",
          verticalAlign: "middle",
        }}
      >
        {medicine.frequency}
      </td>
      <td
        style={{
          padding: "10px",
          textAlign: "center",
          verticalAlign: "middle",
        }}
      >
        {medicine.qty || medicine.quantity}
      </td>
      <td
        style={{
          padding: "10px",
          backgroundColor:
            Number.parseInt(
              medicine.delivered_qty || medicine.deliveredQuantity
            ) < Number.parseInt(medicine.qty || medicine.quantity)
              ? "rgba(255, 255, 0, 0.3)"
              : "transparent",
          textAlign: "center",
          verticalAlign: "middle",
        }}
      >
        {medicine.updated_qty || medicine.deliveredQuantity}
        <span
          data-tooltip-id={`prescription-tooltip-${index}`}
          style={{ display: "inline-block", marginLeft: "8px" }}
        >
          <img
            src={icon2 || "/placeholder.svg"}
            alt="info Icon"
            style={{
              cursor: "pointer",
              width: "24px",
              height: "24px",
              verticalAlign: "middle",
            }}
          />
          <Tooltip
            id={`prescription-tooltip-${index}`}
            place="top"
            effect="solid"
            delayShow={200}
            delayHide={100}
            style={{ zIndex: 9999, maxWidth: "300px" }}
          >
            <div style={{ padding: "8px", textAlign: "left" }}>
              <div style={{ marginBottom: "4px" }}>
                <strong>Quantity:</strong> {medicine.qty || medicine.quantity}
              </div>
              <div>
                <strong>Delivered Quantity:</strong>{" "}
                {medicine.updated_qty}
              </div>
              {Number.parseInt(
                medicine.updated_qty
              ) < Number.parseInt(medicine.qty || medicine.quantity) && (
                  <div style={{ marginTop: "4px", color: "#ffc107" }}>
                    <div>
                      <strong>Delivery Status:</strong> {medicine.delivery_status}
                    </div>
                    <div>
                      <strong>Delivery Type:</strong>{" "}
                      {medicine.delivery_option === "by_hand"
                        ? "By Hand"
                        : medicine.delivery_option === "by_courier"
                          ? "By Courier"
                          : medicine.delivery_option}
                    </div>
                  </div>
                )}
            </div>
          </Tooltip>
        </span>
      </td>
      {isCustom && (
        <td
          style={{
            padding: "10px",
            textAlign: "center",
            verticalAlign: "middle",
          }}
        >
          {medicine.custom_duration_in_days || "N/A"}
        </td>
      )}
      <td style={{ padding: "10px", textAlign: "center", verticalAlign: "middle", }}>
        {isEditMode ? (
          <textarea
            className="form-control"
            value={medicine.how_to_take}
            onChange={(e) => onFieldChange(medicine, "how_to_take", e.target.value)}
            rows={2}
          />
        ) : (
          medicine.how_to_take
        )}
      </td>

      <td style={{ padding: "10px", textAlign: "center", verticalAlign: "middle", }}>

        {isEditMode ? (
          <input
            type="text"
            className="form-control"
            value={medicine.anupan || medicine.anupana_text || ""}
            onChange={(e) => onFieldChange(medicine, "anupan", e.target.value)}
          />
        ) : (
          medicine.anupan || medicine.anupana_text || "-"
        )}

      </td>


      <td
        style={{
          padding: "10px",
          textAlign: "center",
          verticalAlign: "middle",
        }}
      >
        <input
          className="form-check-input"
          type="checkbox"
          checked={isCheckboxChecked(medicine)}
          disabled={isCheckboxDisabled(medicine) || !medicine.medicine_id}
          onChange={(e) =>
            handleMedicineCheckboxChange(medicine, e.target.checked)
          }
          style={{
            opacity:
              isCheckboxDisabled(medicine) || !medicine.medicine_id ? 0.6 : 1,
            cursor:
              isCheckboxDisabled(medicine) || !medicine.medicine_id
                ? "not-allowed"
                : "pointer",
          }}
        />
        {isMedicineOnHold(medicine) && (
          <div
            style={{ fontSize: "0.75rem", color: "#007680", marginTop: "2px" }}
          >
            On Hold
          </div>
        )}
        {!medicine.medicine_id && (
          <div
            style={{ fontSize: "0.75rem", color: "#dc3545", marginTop: "2px" }}
          >
            No ID
          </div>
        )}
      </td>
    </tr>
  );

  return (
    <>
      {/* Show loader overlay when showLoader is true */}
      {showLoader && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <Loader />
        </div>
      )}

      <Modal
        // show={show}
        // onHide={handleModalClose}
        // size="xl"
        // aria-labelledby="contained-modal-title-vcenter"
        // centered
        show={show}
        onHide={handleModalClose}
        size="xl"
        centered
        backdrop="static"
        keyboard={false}
        className="second-modal"
        style={{
          zIndex: 2000,            // always above all other modals
          position: "fixed",       // prevents clipping by first modal
          inset: 0,                // ensure full screen access
        }}
        backdropClassName="second-backdrop"
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            Prescription Details
            <div
              style={{
                fontSize: "0.9rem",
                color: "#6c757d",
                marginTop: "5px",
              }}
            >
              <strong>Patient Name:</strong>{" "}
              {[
                patientDetails.first_name,
                patientDetails.middile_name,
                patientDetails.last_name,
              ]
                .filter(Boolean)
                .join(" ") || "N/A"}{" "}
              &nbsp;|&nbsp;
              <strong>Disease:</strong> {selectedPrescription.diseases || "N/A"}{" "}
              &nbsp;|&nbsp;
              <strong>Gender:</strong> {patientDetails?.gender || "N/A"}
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* Regular Medicines Table */}

          {regularMedicines.length > 0 && (
            <div
              style={{
                border: "2px solid #ddd",
                borderRadius: "5px",
                marginBottom: "24px",
              }}
            >
              <table
                className="table header-border"
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                }}
              >
                <TableHeader isCustom={false} />
                <tbody>
                  {regularMedicines.map((medicine, index) => (
                    <MedicineRow
                      key={`regular-${medicine.medicine_id ||
                        medicine.prescription_medicine_id ||
                        index
                        }`}
                      medicine={medicine}
                      index={index}
                      isCustom={false}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Custom Medicines Table */}
          {customMedicines.length > 0 && (
            <div
              style={{
                border: "2px solid #ddd",
                borderRadius: "5px",
                marginBottom: "0",
              }}
            >
              <h5 className="p-2 border-bottom mb-0">Custom Medicines</h5>
              <table
                className="table header-border"
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                }}
              >
                <TableHeader isCustom={true} />
                <tbody>
                  {customMedicines.map((medicine, index) => (
                    <MedicineRow
                      key={`custom-${medicine.medicine_id ||
                        medicine.prescription_medicine_id ||
                        index
                        }`}
                      medicine={medicine}
                      index={index}
                      isCustom={true}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Show message if no medicines */}
          {regularMedicines.length === 0 && customMedicines.length === 0 && (
            <div className="text-center text-muted py-4">
              No medicines prescribed
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="d-flex justify-content-between align-items-center w-100">
          <div>
            {isEditMode ? (
              <>
                <Button variant="success" onClick={handleSaveEdit}>
                  Save Changes
                </Button>

                <Button variant="secondary" onClick={() => setIsEditMode(false)}>
                  Cancel
                </Button>
              </>
            ) : (
              canEditPrescription() && (
                <Button variant="primary" onClick={handleEdit}>
                  Edit Prescription
                </Button>
              )
            )}

            {selectedMedicines.size > 0 && (
              <Button
                variant="warning"
                size="sm"
                onClick={handleMarkAsHold}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Processing..." : `Mark as Hold`}
              </Button>
            )}
          </div>

          <Button onClick={handleModalClose}>Close</Button>
        </Modal.Footer>

      </Modal>
    </>
  );
};

export default PrescriptionDetailModal;
